import Box from '@mui/material/Box';
import { matchSorter } from 'match-sorter';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import AuraAutoComplete from 'app/main/apps/common/AuraAutoComplete';
import { setCustomer, setSalesPerson, setSupplier } from 'app/main/apps/e-commerce/store/ordersSlice';
import { getListCapabilities } from 'app/main/apps/e-commerce/shared/listCapabilities';
import { countStaffListFilters, STAFF_LIST_SCOPE_LABELS } from './staffListUi';
import { STAFF_LIST_TEST_IDS } from '../staffListTestIds';
import { withoutKey } from 'app/main/apps/common/optionProps';

const autocompleteSx = {
  width: '100% !important',
  minWidth: 180,
};

export function writeSearch(sp) {
  const { location } = window;
  window.history.replaceState(
    null,
    '',
    sp.toString() ? `${location.pathname}?${sp.toString()}` : location.pathname
  );
}

export function pushSearch(sp, navigate) {
  const search = sp.toString();
  writeSearch(sp);
  if (navigate) {
    navigate({ search: search ? `?${search}` : '' }, { replace: true });
  }
}

function resetPage(sp) {
  sp.delete('pageNo');
  return sp;
}

export function applyListType(next, setSelectedFilterType, dispatch, navigate) {
  const { location } = window;
  const sp = new URLSearchParams(location.search);
  const clear = () => Array.from(sp.keys()).forEach((key) => sp.delete(key));

  setSelectedFilterType(next);
  dispatch(setCustomer(null));
  dispatch(setSalesPerson(null));

  switch (next.value) {
    case 'supplier':
      dispatch(setSupplier({ name: 'All', value: 'all', email: '', id: 'all' }));
      clear();
      sp.set('sid', 'all');
      break;
    case 'customer_archive':
    case 'archive_rfq':
    case 'archive_quote':
      dispatch(setSupplier(null));
      clear();
      sp.set('archive', true);
      sp.set('cid', 'all');
      sp.set('sales_person', 'all');
      break;
    case 'supplier_archive':
      dispatch(setSupplier({ name: 'All', value: 'all', email: '', id: 'all' }));
      clear();
      sp.set('sid', 'all');
      sp.set('archive', true);
      break;
    case 'customer':
    case 'active_rfq':
    case 'active_quote':
      dispatch(setSupplier(null));
      clear();
      break;
    case 'back_order':
      dispatch(setSupplier(null));
      clear();
      sp.set('back_order', true);
      break;
    case 'reorder':
      dispatch(setSupplier(null));
      clear();
      sp.set('reorder', true);
      if (!sp.has('date')) {
        sp.set('date', moment().format('YYYY-MM-DD'));
      }
      break;
    default:
      dispatch(setSupplier(null));
      clear();
      break;
  }
  pushSearch(resetPage(sp), navigate);
}

export function setFlag(key, value, setter, navigate) {
  const { location } = window;
  const sp = new URLSearchParams(location.search);
  setter(value);
  sp.set(key, value);
  pushSearch(resetPage(sp), navigate);
}

export function TypeField({ options, value, onChange }) {
  return (
    <label className="staff-list-field" data-testid={STAFF_LIST_TEST_IDS.type}>
      <span className="staff-list-label">Type</span>
      <AuraAutoComplete
        className="staff-list-combo"
        placeholder="Search type"
        inputValues={options}
        onValueChange={(next) => next && onChange(next)}
        defaultValue={options.find((option) => option.value === value) || options[0] || null}
        optionDisplayName="name"
        variant="outlined"
        hideClearable
        isOptionEqualToValue={(option, current) => option?.value === current?.value}
        sxStyle={autocompleteSx}
      />
    </label>
  );
}

export function PeopleField({ label, placeholder, inputValues, defaultValue, onChange, testId }) {
  return (
    <label className="staff-list-field" data-testid={testId}>
      <span className="staff-list-label">{label}</span>
      <AuraAutoComplete
        className="staff-list-combo"
        placeholder={placeholder}
        inputValues={inputValues}
        onValueChange={onChange}
        defaultValue={defaultValue || null}
        optionDisplayName="name"
        variant="outlined"
        hideClearable={false}
        isOptionEqualToValue={(option, value1) => Boolean(option?.name && value1?.name && option.name === value1.name)}
        renderOption={(renderprops, option) => (
          <Box component="li" {...withoutKey(renderprops)} key={option.id} className={`${renderprops.className} !block py-2 px-10`}>
            <div className="font-medium">{option.name}</div>
            <div className="font-light text-10 text-gray-400">{option.email}</div>
          </Box>
        )}
        filterOptions={(options, { inputValue }) =>
          matchSorter(options, inputValue, {
            keys: ['name'],
            threshold: matchSorter.rankings.STARTS_WITH,
          })
        }
        sxStyle={autocompleteSx}
      />
    </label>
  );
}

export function ReorderDateField({ value, onChange }) {
  const formatted = value ? moment(value).format('YYYY-MM-DD') : '';
  return (
    <label className="staff-list-field" data-testid={STAFF_LIST_TEST_IDS.reorderDate}>
      <span className="staff-list-label">Order date</span>
      <input
        type="date"
        className="staff-list-select"
        value={formatted}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}

function StaffListFilters({
  user,
  listType,
  isManage,
  options,
  filterType,
  setSelectedFilterType,
  includeInvoice,
  setIncludeInvoice,
  includeArchive,
  setIncludeArchive,
  showOnlyVAS,
  setShowOnlyVAS,
  actionableOnly,
  setActionableOnly,
  reorderDate,
  setReorderDate,
  onReset,
}) {
  const dispatch = useDispatch();
  const isReorderMode = filterType?.value === 'reorder';
  const can = getListCapabilities(user, listType, { canManage: isManage, isReorderMode });
  const isSupplierType = ['supplier', 'supplier_archive'].includes(filterType?.value);
  const customers = useSelector((state) => state.eCommerceApp.orders.customersListItems) || [];
  const suppliers = useSelector((state) => state.eCommerceApp.orders.suppliersList) || [];
  const salesPersons = useSelector((state) => state.eCommerceApp.orders.salesPersonListItems) || [];
  const customer = useSelector((state) => state.eCommerceApp.orders.customer);
  const supplier = useSelector((state) => state.eCommerceApp.orders.supplier);
  const salesPerson = useSelector((state) => state.eCommerceApp.orders.salesPerson);
  const activeCount = countStaffListFilters({
    filterType,
    defaultType: options?.[0]?.value,
    customer: isSupplierType ? supplier : customer,
    salesPerson,
    includeInvoice,
    includeArchive,
    showOnlyVAS,
    actionableOnly,
  });

  const handlePeopleChange = (value) => {
    const { location } = window;
    const sp = new URLSearchParams(location.search);
    sp.delete('sid');
    sp.delete('cid');
    if (isSupplierType) {
      dispatch(setSupplier(value));
      dispatch(setCustomer(null));
      if (value) {
        sp.set('sid', value.id);
      } else {
        sp.set('sid', 'all');
      }
    } else {
      dispatch(setCustomer(value));
      dispatch(setSupplier(null));
      if (value) {
        sp.set('cid', value.id);
      }
    }
    writeSearch(sp);
  };

  const handleSalesChange = (value) => {
    const { location } = window;
    const sp = new URLSearchParams(location.search);
    sp.delete('sales_person');
    dispatch(setSalesPerson(value));
    if (value?.id) {
      sp.set('sales_person', value.id);
    }
    writeSearch(sp);
  };

  return (
    <section className="staff-list-filters">
      {options?.length > 0 && (
        <label className="staff-list-field">
          <span className="staff-list-label">Type</span>
          <select
            className="staff-list-select"
            value={filterType?.value || options[0]?.value}
            onChange={(event) => {
              const next = options.find((option) => option.value === event.target.value);
              if (next) {
                applyListType(next, setSelectedFilterType, dispatch);
              }
            }}
          >
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.name}
              </option>
            ))}
          </select>
        </label>
      )}

      {!isReorderMode && (
        <PeopleField
          label={isSupplierType ? 'Supplier' : 'Customer'}
          placeholder={isSupplierType ? 'All suppliers' : 'All customers'}
          inputValues={isSupplierType ? [{ name: 'All', value: 'all', email: '', id: 'all' }, ...suppliers] : customers}
          defaultValue={isSupplierType ? supplier : customer}
          onChange={handlePeopleChange}
        />
      )}

      {!isReorderMode && !isSupplierType && (
        <PeopleField
          label="Sales person"
          placeholder="All sales persons"
          inputValues={salesPersons}
          defaultValue={salesPerson}
          onChange={handleSalesChange}
        />
      )}

      {isReorderMode && (
        <ReorderDateField value={reorderDate} onChange={setReorderDate} />
      )}

      {(can.filterIncludeInvoice || can.filterIncludeArchive || can.filterShowOnlyVAS || can.filterActionableOnly) && (
        <>
          <div className="staff-list-divider" />
          <div className="staff-list-scope">
            <span className="staff-list-label">Scope</span>
            <div className="staff-list-scope-row">
              {can.filterIncludeInvoice && (
                <button
                  type="button"
                  className={`staff-list-chip${includeInvoice ? ' is-on' : ''}`}
                  onClick={() => setFlag('include_invoice_status', !includeInvoice, setIncludeInvoice)}
                >
                  {STAFF_LIST_SCOPE_LABELS.invoiced}
                </button>
              )}
              {can.filterIncludeArchive && (
                <button
                  type="button"
                  className={`staff-list-chip${includeArchive ? ' is-on' : ''}`}
                  onClick={() => setFlag('include_archive', !includeArchive, setIncludeArchive)}
                >
                  {STAFF_LIST_SCOPE_LABELS.archived}
                </button>
              )}
              {can.filterShowOnlyVAS && (
                <button
                  type="button"
                  className={`staff-list-chip${showOnlyVAS ? ' is-on' : ''}`}
                  onClick={() => setFlag('vas_order', !showOnlyVAS, setShowOnlyVAS)}
                >
                  {STAFF_LIST_SCOPE_LABELS.vas}
                </button>
              )}
              {can.filterActionableOnly && (
                <button
                  type="button"
                  className={`staff-list-chip${actionableOnly ? ' is-on' : ''}`}
                  onClick={() => setFlag('actionableOnly', !actionableOnly, setActionableOnly)}
                >
                  {STAFF_LIST_SCOPE_LABELS.actionable}
                </button>
              )}
            </div>
          </div>
        </>
      )}

      <div className="staff-list-filter-meta">
        <span className="staff-list-filter-count">
          <strong>{activeCount}</strong> filters active
        </span>
        <button type="button" className="staff-list-btn" onClick={onReset}>
          Reset filters
        </button>
      </div>
    </section>
  );
}

export default StaffListFilters;
